<style src="./Chat.css"></style>

<template lang="html">
  <section class="chat-all">
    <header class="chat-header">
      <div class="message-avatar">
        {{ secondaryFirstLetter }}
      </div>
      <div class="chat-header-name">
        {{ secondaryDisplayName }}
      </div>
      <button
        type="button"
        class="chat-close-btn"
        aria-label="Fechar chat"
        title="Fechar chat"
        @click="closeChat"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </header>

    <main ref="messages" class="chat-messages-container">
      <p v-if="!chatMessages.length && !isLoading" class="chat-empty">
        Nenhuma mensagem ainda. Envie a primeira.
      </p>
      <div
        class="chat-message"
        v-for="msg in chatMessages"
        :key="msg.id"
        :class="['message-' + msg.sender]"
      >
        <div class="message-avatar">
          {{ msg.sender === "secondary" ? secondaryFirstLetter : primaryFirstLetter }}
        </div>
        <div class="message-wrapper">
          <div class="message-content">
            <span class="chat-message-text">{{ msg.text }}</span>
          </div>
          <span class="message-time">
            <span v-if="msg.sender === 'primary'">Você •</span>
            <span v-else>{{ secondaryDisplayName }} •</span>
            <span>{{ msg.createdAt }}</span>
          </span>
        </div>
      </div>
    </main>

    <footer class="chat-input-container">
      <input
        v-model="message"
        type="text"
        class="chat-input"
        placeholder="Digite sua mensagem aqui..."
        @keyup.enter="serviceSendMessage"
      />
      <button
        type="button"
        class="chat-send-btn"
        :disabled="isSending || !message.trim()"
        @click="serviceSendMessage"
      >
        <IconSend />
      </button>
    </footer>
  </section>
</template>

<script>
import { sendMessage, getMessages } from "@/services/service-api";
import websocketService from "@/services/websocket-service";
import { normalizeDialerUid } from "@/agora-service/AgoraUid.js";

import IconSend from "../icons/IconSend.vue";

export default {
  name: "Chat",
  components: {
    IconSend,
  },

  props: {
    primaryUID: {
      type: [String, Number],
      required: true,
    },
    secondaryUID: {
      type: [String, Number],
      required: true,
    },
    roomName: {
      type: String,
      default: null,
    },
    showMessages: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => null,
    },
    primaryName: {
      type: String,
      default: "",
    },
    secondaryName: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      message: "",
      chatMessages: [],
      room: {
        id: null,
        name: null,
      },
      removeMessageListener: null,
      detached: false,
      isLoading: false,
      isSending: false,
    };
  },

  computed: {
    secondaryDisplayName() {
      const name = String(this.secondaryName || this.options?.name || "").trim();
      return name || "Atendimento";
    },
    primaryDisplayName() {
      return String(this.primaryName || "").trim();
    },
    secondaryFirstLetter() {
      return this.secondaryDisplayName.charAt(0).toUpperCase() || "A";
    },
    primaryFirstLetter() {
      if (this.primaryDisplayName) {
        return this.primaryDisplayName.charAt(0).toUpperCase();
      }
      return "V";
    },
    localUid() {
      return normalizeDialerUid(this.primaryUID);
    },
    remoteUid() {
      return normalizeDialerUid(this.secondaryUID);
    },
    socketRoom() {
      const name = this.room.name || this.roomName;
      if (!name || !this.localUid) return null;
      return `${name}_${this.localUid}`;
    },
  },

  watch: {
    socketRoom: {
      immediate: true,
      handler(newRoom) {
        if (newRoom && !this.detached) {
          this.initializeWebSocket(newRoom);
        }
      },
    },
  },

  beforeDestroy() {
    if (this.removeMessageListener) {
      this.removeMessageListener();
    }
    if (this.detached) return;
    if (this.roomName && this.localUid) {
      websocketService.leaveRoom({
        roomName: this.roomName,
        localUid: this.localUid,
        remoteUid: this.remoteUid ?? null
      });
    }
  },

  async mounted() {
    await this.chargeMessages();
  },

  methods: {
    closeChat() {
      this.$emit("close-chat");
    },

    detach() {
      this.detached = true;
      if (this.removeMessageListener) {
        this.removeMessageListener();
        this.removeMessageListener = null;
      }
      const name = this.room.name || this.roomName;
      if (name && this.localUid) {
        websocketService.leaveRoom({ roomName: name, localUid: this.localUid });
      }
    },

    initializeWebSocket(socketRoom) {
      websocketService.connect(socketRoom);
      if (this.removeMessageListener) {
        this.removeMessageListener();
      }

      this.removeMessageListener = websocketService.onNewMessage((message) => {
        this.$emit('onNewMessage', message)
        this.handleNewMessage();
      });
    },

    async handleNewMessage() {
      await this.chargeMessages();
    },

    formatMessageTime(value) {
      const date = value ? new Date(value) : new Date();
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    mapServerMessage(msg) {
      const senderUid = normalizeDialerUid(msg.emissorUid || msg.senderUID);
      return {
        id: msg.id || `${senderUid}-${msg.createdAt}-${msg.message}`,
        sender: senderUid === this.localUid ? "primary" : "secondary",
        text: msg.message || "",
        createdAt: this.formatMessageTime(msg.createdAt),
      };
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    async serviceSendMessage() {
      const msgTrim = this.message.trim();
      if (!msgTrim || this.isSending) return;

      this.isSending = true;
      this.message = "";

      try {
        const response = await sendMessage({
          primaryUID: this.localUid,
          secondaryUID: this.remoteUid,
          message: msgTrim,
        });

        if (response?.status === "error") {
          this.message = msgTrim;
          return;
        }

        await this.chargeMessages();
      } catch (error) {
        this.message = msgTrim;
      } finally {
        this.isSending = false;
      }
    },

    async chargeMessages() {
      this.isLoading = true;
      try {
        const getMessagesResponse = await getMessages({
          primaryUID: this.localUid,
          secondaryUID: this.remoteUid,
        });
        if (getMessagesResponse.status !== "success") {
          return;
        }
        this.room = {
          id: getMessagesResponse.data.salas.id,
          name: getMessagesResponse.data.salas.nome,
        };
        this.chatMessages = (getMessagesResponse.data.mensagens || []).map(
          (msg) => this.mapServerMessage(msg),
        );
        this.scrollToBottom();
      } catch (error) {
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
